import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { restaurantsAPI } from '../../services/api';
import Header from '../../components/layout/Header';
import { MapPin, Phone, Globe, Instagram } from 'lucide-react';

export default function RestaurantPage() {
  const { id } = useParams<{ id: string }>();

  const { data: restaurantData, isLoading: restaurantLoading } = useQuery({
    queryKey: ['restaurant', id],
    queryFn: () => restaurantsAPI.getById(parseInt(id!)).then((res) => res.data),
    enabled: !!id,
  });

  const { data: offersData, isLoading: offersLoading } = useQuery({
    queryKey: ['restaurant-offers', id],
    queryFn: () => restaurantsAPI.getOffers(parseInt(id!)).then((res) => res.data),
    enabled: !!id,
  });

  if (restaurantLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow animate-pulse h-96" />
        </div>
      </div>
    );
  }

  const restaurant = restaurantData?.data;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
          {/* Cover Image */}
          {restaurant?.coverImageUrl && (
            <div className="h-64 bg-gray-200">
              <img
                src={restaurant.coverImageUrl}
                alt={restaurant.name}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Restaurant Info */}
          <div className="p-8">
            <div className="flex items-start gap-6 mb-6">
              {restaurant?.logoUrl && (
                <img
                  src={restaurant.logoUrl}
                  alt={restaurant.name}
                  className="w-24 h-24 rounded-lg object-cover"
                />
              )}
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{restaurant?.name}</h1>
                {restaurant?.cuisineTypes && restaurant.cuisineTypes.length > 0 && (
                  <p className="text-gray-600 mb-4">{restaurant.cuisineTypes.join(', ')}</p>
                )}
                <p className="text-gray-700">{restaurant?.description}</p>
              </div>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-6 border-t">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gray-400 mt-1" />
                <div>
                  <p className="text-gray-900">{restaurant?.address}</p>
                  <p className="text-gray-600">{restaurant?.area}, Udaipur</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <a
                  href={`tel:${restaurant?.phone}`}
                  className="text-red-600 hover:text-red-700 font-medium"
                >
                  {restaurant?.phone}
                </a>
              </div>
              {restaurant?.website && (
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-gray-400" />
                  <a
                    href={restaurant.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-red-600 hover:text-red-700"
                  >
                    Website
                  </a>
                </div>
              )}
              {restaurant?.instagramUrl && (
                <div className="flex items-center gap-3">
                  <Instagram className="w-5 h-5 text-gray-400" />
                  <a
                    href={restaurant.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-red-600 hover:text-red-700"
                  >
                    Instagram
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Active Offers */}
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Current Offers</h2>
        {offersLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-lg shadow h-80 animate-pulse" />
            ))}
          </div>
        ) : offersData?.data && offersData.data.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {offersData.data.map((offer: any) => (
              <Link
                key={offer.id}
                to={`/offers/${offer.id}`}
                className="bg-white rounded-lg shadow overflow-hidden hover:shadow-lg transition"
              >
                <div className="relative h-48 bg-gray-200">
                  {offer.imageUrl ? (
                    <img
                      src={offer.imageUrl}
                      alt={offer.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      No Image
                    </div>
                  )}
                  {offer.discountPercentage && (
                    <div className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1 rounded-full font-bold text-sm">
                      {offer.discountPercentage}% OFF
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{offer.title}</h3>
                  <div className="flex items-center gap-2">
                    {offer.offerPrice && (
                      <span className="text-xl font-bold text-green-600">₹{offer.offerPrice}</span>
                    )}
                    {offer.originalPrice && (
                      <span className="text-gray-400 line-through">₹{offer.originalPrice}</span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-600">No active offers at the moment.</p>
          </div>
        )}
      </div>
    </div>
  );
}
